/**
 * Editor de plantillas basado en TipTap — portado del editor de plantillas de
 * bankivo (src/modules/configuracion/plantillas/TiptapEditor.js):
 *
 *   - Las variables {{...}} son nodos atómicos (chips): no se pueden romper
 *     editando dentro, se borran de un solo golpe y se distinguen del texto.
 *   - Imágenes redimensionables con agarres en las esquinas y alineación
 *     propia (izq/centro/der) desde una mini-barra flotante.
 *   - Importación de Word en el navegador (mammoth) con styleMap y avisos
 *     de conversión — sin viaje al servidor.
 *   - El editor es no-controlado (onUpdate notifica hacia afuera), así que
 *     escribir no re-renderiza el diálogo completo en cada tecla.
 *
 * Acepta HTML guardado por el editor anterior (Quill): las clases
 * ql-align-* se normalizan a style="text-align:..." al cargar.
 */
import React, { useEffect, useImperativeHandle, forwardRef, useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Node, Extension, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DecorationSet, Decoration } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, FontSize } from '@tiptap/extension-text-style';
import ImageExt from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import mammoth from 'mammoth';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatIndentIncrease,
  FormatIndentDecrease,
  FormatLineSpacing,
  TableChartOutlined,
  Image as ImageIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  UploadFile as UploadFileIcon,
  InsertPageBreak as InsertPageBreakIcon
} from '@mui/icons-material';

const CHIP_BLUE = '#1976d2';
const CHIP_BLUE_BG = '#e3f2fd';

// Tamaños de letra en puntos (misma escala que Word, que es la referencia
// del usuario que redacta documentos legales).
const TAMANOS_LETRA = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

const VariableNodeView = ({ node }) => {
  return (
    <NodeViewWrapper as="span" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <Chip
        label={`{{${node.attrs.name}}}`}
        size="small"
        color="primary"
        sx={{
          height: 22,
          mx: 0.25,
          fontWeight: 600,
          backgroundColor: CHIP_BLUE_BG,
          color: CHIP_BLUE,
          border: `1px solid ${CHIP_BLUE}`,
          '& .MuiChip-label': { px: 0.75, fontSize: '0.78rem' }
        }}
      />
    </NodeViewWrapper>
  );
};

const Variable = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  // Acepta marcas (tamaño de letra, negrita...) para que el valor sustituido
  // herede el formato del texto que rodea al chip al generar el documento.
  marks: '_',

  addAttributes() {
    return {
      name: { default: '' },
      label: { default: '' }
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const { name } = HTMLAttributes;
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-variable': '', 'data-name': name }),
      `{{${name}}}`
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VariableNodeView);
  }
});

// Salto de página: en el editor se ve como una línea punteada con etiqueta;
// al imprimir, el CSS (`[data-page-break]`) fuerza el corte de hoja, y en la
// descarga a Word el backend lo convierte en un salto de página real.
const PageBreakView = ({ selected }) => (
  <NodeViewWrapper
    as="div"
    contentEditable={false}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      margin: '0.8em 0',
      userSelect: 'none',
      outline: selected ? `2px solid ${CHIP_BLUE}` : 'none',
      outlineOffset: 2,
      borderRadius: 4
    }}
  >
    <span style={{ flex: 1, borderTop: '2px dashed #9aa4b1' }} />
    <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7684', letterSpacing: '0.08em' }}>
      SALTO DE PÁGINA
    </span>
    <span style={{ flex: 1, borderTop: '2px dashed #9aa4b1' }} />
  </NodeViewWrapper>
);

const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  selectable: true,

  parseHTML() {
    return [{ tag: 'div[data-page-break]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-page-break': 'true' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(PageBreakView);
  }
});

const ResizableImageNodeView = ({ node, updateAttributes, selected, editor, getPos }) => {
  const imgRef = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: node.attrs.width,
    height: node.attrs.height
  });
  const align = node.attrs.align || 'left';

  useEffect(() => {
    setDimensions({ width: node.attrs.width, height: node.attrs.height });
  }, [node.attrs.width, node.attrs.height]);

  const startResize = useCallback((corner) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const img = imgRef.current;
    if (!img) return;

    const startX = e.clientX;
    const startWidth = img.offsetWidth;
    const startHeight = img.offsetHeight;
    const aspect = startWidth / startHeight;
    const dirX = corner === 'tl' || corner === 'bl' ? -1 : 1;

    const onMove = (ev) => {
      const dx = (ev.clientX - startX) * dirX;
      const newWidth = Math.max(40, startWidth + dx);
      const newHeight = Math.round(newWidth / aspect);
      setDimensions({ width: newWidth, height: newHeight });
    };
    const onUp = (ev) => {
      const dx = (ev.clientX - startX) * dirX;
      const newWidth = Math.max(40, startWidth + dx);
      const newHeight = Math.round(newWidth / aspect);
      // Redimensionar a mano fija un ancho en px: sale del modo ancho completo.
      updateAttributes({ width: newWidth, height: newHeight, fullwidth: false });
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [updateAttributes]);

  const wrap = node.attrs.wrap || 'none';
  const fullwidth = !!node.attrs.fullwidth;
  const fullbleed = !!node.attrs.fullbleed;
  // ¿Es el primer nodo del documento? Solo ahí "borde a borde" también
  // elimina el margen superior (banner de cabecera).
  const esPrimera = typeof getPos === 'function' && getPos() === 0;

  const setAlign = (value) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    updateAttributes({ align: value, wrap: 'none', fullwidth: false, fullbleed: false });
  };

  const setWrap = (value) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    updateAttributes({ wrap: value, fullwidth: false, fullbleed: false });
  };

  const setFullwidth = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Ancho completo es un bloque: sin flotado y sin ancho fijo en px.
    updateAttributes({
      fullwidth: !fullwidth,
      fullbleed: false,
      wrap: 'none',
      width: null,
      height: null
    });
  };

  const setFullbleed = (e) => {
    e.preventDefault();
    e.stopPropagation();
    updateAttributes({
      fullbleed: !fullbleed,
      fullwidth: false,
      wrap: 'none',
      width: null,
      height: null
    });
  };

  const wrapperAlign = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
  // Con "texto al lado" la imagen flota y los párrafos siguientes fluyen a su
  // costado, como el ajuste de texto de Word.
  let wrapperStyle;
  if (fullbleed) {
    // Compensa los márgenes de la hoja del editor (los mismos que @page pone
    // al imprimir; llegan como variables CSS de la hoja): la imagen toca los
    // bordes del papel.
    wrapperStyle = {
      width: 'calc(100% + var(--mizq, 18mm) + var(--mder, 18mm))',
      maxWidth: 'none',
      margin: esPrimera
        ? 'calc(-1 * var(--msup, 18mm)) calc(-1 * var(--mder, 18mm)) 0.5em calc(-1 * var(--mizq, 18mm))'
        : '0.5em calc(-1 * var(--mder, 18mm)) 0.5em calc(-1 * var(--mizq, 18mm))'
    };
  } else if (fullwidth) {
    wrapperStyle = { margin: '0.5em 0', width: '100%' };
  } else if (wrap === 'none') {
    wrapperStyle = { textAlign: wrapperAlign, margin: '0.5em 0' };
  } else {
    wrapperStyle = {
      float: wrap,
      margin: wrap === 'left' ? '0.3em 1em 0.5em 0' : '0.3em 0 0.5em 1em',
      maxWidth: '100%'
    };
  }
  const isEditable = editor?.isEditable;

  const handleStyle = {
    position: 'absolute',
    width: 10,
    height: 10,
    backgroundColor: CHIP_BLUE,
    border: '2px solid white',
    borderRadius: 2,
    zIndex: 2
  };

  return (
    <NodeViewWrapper as="div" style={wrapperStyle}>
      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: fullbleed ? 'none' : '100%',
          width: fullwidth || fullbleed ? '100%' : undefined,
          outline: selected ? `2px solid ${CHIP_BLUE}` : '2px solid transparent',
          outlineOffset: 2,
          lineHeight: 0
        }}
      >
        <img
          ref={imgRef}
          src={node.attrs.src}
          alt={node.attrs.alt || ''}
          width={fullwidth || fullbleed ? undefined : dimensions.width || undefined}
          height={fullwidth || fullbleed ? undefined : dimensions.height || undefined}
          style={{
            maxWidth: fullbleed ? 'none' : '100%',
            width: fullwidth || fullbleed ? '100%' : undefined,
            height: 'auto',
            display: 'block',
            cursor: isEditable ? 'pointer' : 'default'
          }}
          draggable={false}
        />
        {selected && isEditable && (
          <>
            <div onMouseDown={startResize('tl')} style={{ ...handleStyle, top: -5, left: -5, cursor: 'nwse-resize' }} />
            <div onMouseDown={startResize('tr')} style={{ ...handleStyle, top: -5, right: -5, cursor: 'nesw-resize' }} />
            <div onMouseDown={startResize('bl')} style={{ ...handleStyle, bottom: -5, left: -5, cursor: 'nesw-resize' }} />
            <div onMouseDown={startResize('br')} style={{ ...handleStyle, bottom: -5, right: -5, cursor: 'nwse-resize' }} />
            <div
              contentEditable={false}
              style={{
                position: 'absolute',
                top: -36,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: 2,
                background: CHIP_BLUE,
                borderRadius: 4,
                padding: 2,
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                zIndex: 3
              }}
            >
              {[
                { id: 'left', label: 'Izq', accion: setAlign('left'), activo: !fullwidth && wrap === 'none' && align === 'left' },
                { id: 'center', label: 'Centro', accion: setAlign('center'), activo: !fullwidth && wrap === 'none' && align === 'center' },
                { id: 'right', label: 'Der', accion: setAlign('right'), activo: !fullwidth && wrap === 'none' && align === 'right' },
                { id: 'wrap-left', label: '◧ Texto al lado', accion: setWrap('left'), activo: wrap === 'left' },
                { id: 'wrap-right', label: 'Texto al lado ◨', accion: setWrap('right'), activo: wrap === 'right' },
                { id: 'fullwidth', label: '⬌ Ancho total', accion: setFullwidth, activo: fullwidth },
                { id: 'fullbleed', label: '⛶ Borde a borde', accion: setFullbleed, activo: fullbleed }
              ].map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onMouseDown={b.accion}
                  style={{
                    background: b.activo ? 'white' : 'transparent',
                    color: b.activo ? CHIP_BLUE : 'white',
                    border: 'none',
                    borderRadius: 3,
                    padding: '2px 8px',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </NodeViewWrapper>
  );
};

const ResizableImage = ImageExt.extend({
  name: 'image',
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => {
          const w = el.getAttribute('width');
          return w ? parseInt(w, 10) || null : null;
        },
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {})
      },
      height: {
        default: null,
        parseHTML: (el) => {
          const h = el.getAttribute('height');
          return h ? parseInt(h, 10) || null : null;
        },
        renderHTML: (attrs) => (attrs.height ? { height: attrs.height } : {})
      },
      align: {
        default: 'left',
        parseHTML: (el) => el.getAttribute('data-align') || 'left',
        renderHTML: (attrs) => (attrs.align ? { 'data-align': attrs.align } : {})
      },
      // 'none' = bloque (texto arriba/abajo); 'left'/'right' = la imagen
      // flota y el texto fluye a su lado (ajuste de texto tipo Word).
      wrap: {
        default: 'none',
        parseHTML: (el) => el.getAttribute('data-wrap') || 'none',
        renderHTML: (attrs) =>
          attrs.wrap && attrs.wrap !== 'none' ? { 'data-wrap': attrs.wrap } : {}
      },
      // Ancho completo: la imagen se estira al 100% del ancho del documento
      // (banners/membretes de cabecera). Ignora width/height fijos.
      fullwidth: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-fullwidth') === 'true',
        renderHTML: (attrs) => (attrs.fullwidth ? { 'data-fullwidth': 'true' } : {})
      },
      // Borde a borde: se sale de los márgenes de la hoja (18mm) y toca los
      // bordes del papel; si además es lo primero del documento, también el
      // borde superior. Para banners de cabecera sin margen.
      fullbleed: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-fullbleed') === 'true',
        renderHTML: (attrs) => (attrs.fullbleed ? { 'data-fullbleed': 'true' } : {})
      }
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageNodeView);
  }
});

// Convierte los {{var}} de texto plano en <span data-variable> para que
// TipTap los parsee como nodos Variable.
const wrapVariableTokens = (html) => {
  if (!html) return '';
  return html.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, name) =>
    `<span data-variable data-name="${name}">{{${name}}}</span>`
  );
};

// Las plantillas guardadas por el editor anterior (Quill) marcan la alineación
// con clases ql-align-*; TipTap solo entiende style="text-align:...".
const normalizarHtmlQuill = (html) => {
  if (!html) return '';
  return html.replace(/class="([^"]*ql-align-(center|right|justify)[^"]*)"/g,
    (_, cls, align) => `class="${cls}" style="text-align:${align}"`);
};

const prepararContenido = (html) => wrapVariableTokens(normalizarHtmlQuill(html));

// Al cargar HTML guardado, ProseMirror por defecto colapsa rachas de espacios
// a uno solo (regla de HTML), lo que aplasta las líneas de firma tipo
// "______    ______". Con 'full' los espacios sobreviven el ciclo
// guardar → recargar tal como se escribieron.
const OPCIONES_PARSEO = { preserveWhitespace: 'full' };

// Altura útil de una hoja carta en px CSS (1in = 96px): 11in menos los
// márgenes de impresión de 18mm arriba y abajo.
// Interlineado y sangría por bloque (párrafos y títulos), al estilo Docs.
// Viajan como estilos inline, así que la impresión los respeta sin más.
const EstiloParrafo = Extension.create({
  name: 'estiloParrafo',
  addGlobalAttributes() {
    return [
      {
        // Tabla sin bordes: para maquetar bloques lado a lado (líneas de
        // firma en dos columnas) sin que se impriman las líneas de la tabla.
        // En el editor las celdas se marcan con un guía punteado; al
        // imprimir y en Word no sale ningún borde.
        types: ['table'],
        attributes: {
          sinbordes: {
            default: false,
            parseHTML: (el) => el.getAttribute('data-sinbordes') === 'true',
            renderHTML: (attrs) => (attrs.sinbordes ? { 'data-sinbordes': 'true' } : {})
          }
        }
      },
      {
        types: ['paragraph', 'heading'],
        attributes: {
          lineheight: {
            default: null,
            parseHTML: (el) => el.style.lineHeight || null,
            renderHTML: (attrs) =>
              attrs.lineheight ? { style: `line-height: ${attrs.lineheight}` } : {}
          },
          indent: {
            default: 0,
            parseHTML: (el) => Math.round(parseInt(el.style.marginLeft, 10) / 40) || 0,
            renderHTML: (attrs) =>
              attrs.indent ? { style: `margin-left: ${attrs.indent * 40}px` } : {}
          }
        }
      }
    ];
  },
  addCommands() {
    return {
      setLineHeight:
        (valor) =>
        ({ commands }) =>
          ['paragraph', 'heading'].every((t) =>
            commands.updateAttributes(t, { lineheight: valor })
          ),
      cambiarSangria:
        (delta) =>
        ({ editor: ed, commands }) => {
          // Dentro de una lista, la sangría anida/des-anida el item (como
          // Docs); fuera, mueve el margen del bloque en pasos de 40px.
          if (ed.isActive('listItem')) {
            return delta > 0
              ? commands.sinkListItem('listItem')
              : commands.liftListItem('listItem');
          }
          const actual =
            ed.getAttributes('paragraph').indent || ed.getAttributes('heading').indent || 0;
          const nuevo = Math.max(0, Math.min(8, actual + delta));
          return ['paragraph', 'heading'].every((t) =>
            commands.updateAttributes(t, { indent: nuevo })
          );
        }
    };
  },
  addKeyboardShortcuts() {
    return {
      Tab: () =>
        this.editor.isActive('listItem')
          ? this.editor.commands.sinkListItem('listItem')
          : this.editor.commands.cambiarSangria(1),
      'Shift-Tab': () =>
        this.editor.isActive('listItem')
          ? this.editor.commands.liftListItem('listItem')
          : this.editor.commands.cambiarSangria(-1)
    };
  }
});

// Paginación visual: bandas grises entre bloques donde la impresión cortará
// de hoja, para que el lienzo se vea como hojas apiladas (tipo Docs). Las
// posiciones las calcula el componente (conoce alto de hoja, pie y banner) y
// las publica con setMeta; el plugin solo mantiene las decoraciones.
const clavePaginas = new PluginKey('paginasVisuales');
const PaginasVisuales = Extension.create({
  name: 'paginasVisuales',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: clavePaginas,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, decos) {
            const meta = tr.getMeta(clavePaginas);
            if (meta) return meta;
            return decos.map(tr.mapping, tr.doc);
          }
        },
        props: {
          decorations(state) {
            return this.getState(state);
          }
        }
      })
    ];
  }
});

// Banda gris de "cambio de hoja" (paginación visual). Se estila con la clase
// .salto-hoja-visual desde el sx del lienzo.
function crearBandaHoja(numero) {
  const banda = document.createElement('div');
  banda.className = 'salto-hoja-visual';
  banda.setAttribute('contenteditable', 'false');
  const etiqueta = document.createElement('span');
  etiqueta.textContent = `Hoja ${numero}`;
  banda.appendChild(etiqueta);
  return banda;
}

// `compacto`: hoja de altura libre sin guías de corte, para secciones cortas
// como el pie de página de la plantilla. `reservaPie`: alto (px) que el pie
// de página ocupará en cada hoja impresa — las guías de fin de hoja lo
// descuentan para seguir coincidiendo con la impresión real. `hoja`:
// { ancho, alto, margenes: {sup, der, inf, izq} } — tamaño y márgenes de la
// plantilla; sin prop, carta con 18mm.
const aPx = (v) => (String(v).endsWith('in') ? parseFloat(v) * 96 : (parseFloat(v) / 25.4) * 96);

const TiptapEditor = forwardRef((props, ref) => {
  const { initialContent = '', onChange, onAlert, onFocus, compacto, reservaPie, hoja } = props;
  const margenes = hoja?.margenes || { sup: 18, der: 18, inf: 18, izq: 18 };
  const anchoHoja = hoja?.ancho || '8.5in';
  const altoHoja = hoja?.alto || '11in';
  const altoUtilPx = aPx(altoHoja) - ((margenes.sup + margenes.inf) / 25.4) * 96;
  const supPx = (margenes.sup / 25.4) * 96;
  // Firma estable para los efectos (el objeto hoja cambia de identidad en
  // cada render del padre).
  const firmaHoja = `${anchoHoja}|${altoHoja}|${margenes.sup},${margenes.der},${margenes.inf},${margenes.izq}`;
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const hojaRef = useRef(null);
  const [importing, setImporting] = React.useState(false);

  // Imágenes pegadas del portapapeles o arrastradas al documento (Docs-like).
  const insertarImagenesExternas = useCallback(
    (view, data, coords) => {
      const archivos = Array.from(data?.files || []).filter((f) => f.type.startsWith('image/'));
      if (!archivos.length) return false;
      archivos.forEach((archivo) => {
        if (archivo.size > 2 * 1024 * 1024) {
          onAlert?.('La imagen es muy grande. Máximo 2MB.', 'error');
          return;
        }
        const lector = new FileReader();
        lector.onload = (ev) => {
          const nodo = view.state.schema.nodes.image.create({ src: ev.target.result });
          let tr = view.state.tr;
          if (coords) {
            const punto = view.posAtCoords(coords);
            if (punto) tr = tr.insert(punto.pos, nodo);
            else tr = tr.replaceSelectionWith(nodo);
          } else {
            tr = tr.replaceSelectionWith(nodo);
          }
          view.dispatch(tr);
        };
        lector.readAsDataURL(archivo);
      });
      return true;
    },
    [onAlert]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      FontSize,
      TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right', 'justify'] }),
      ResizableImage.configure({ inline: false, allowBase64: true }),
      TableKit.configure({ table: { resizable: true } }),
      EstiloParrafo,
      PaginasVisuales,
      Variable,
      PageBreak
    ],
    editorProps: {
      handlePaste: (view, event) => insertarImagenesExternas(view, event.clipboardData),
      handleDrop: (view, event, _slice, moved) =>
        moved
          ? false
          : insertarImagenesExternas(view, event.dataTransfer, {
              left: event.clientX,
              top: event.clientY
            })
    },
    content: prepararContenido(initialContent),
    parseOptions: OPCIONES_PARSEO,
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    // Avisa al padre cuando este editor toma el foco (el panel de variables
    // inserta en el último editor enfocado: cuerpo o pie de página).
    onFocus: () => {
      onFocus?.();
    }
  });

  useImperativeHandle(ref, () => ({
    setContent: (html) => {
      if (editor) editor.commands.setContent(prepararContenido(html || ''), { parseOptions: OPCIONES_PARSEO });
    },
    insertVariable: (name, label) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContent({ type: 'variable', attrs: { name, label: label || name } })
        .insertContent(' ')
        .run();
    },
    getHTML: () => (editor ? editor.getHTML() : ''),
    getText: () => (editor ? editor.getText() : ''),
    // Alto del contenido en px (para que el editor principal reserve el
    // espacio del pie de página en sus guías de fin de hoja).
    getAlturaContenido: () => hojaRef.current?.querySelector('.ProseMirror')?.offsetHeight || 0,
    focus: () => editor?.commands.focus()
  }));

  useEffect(() => {
    return () => editor?.destroy();
  }, [editor]);

  // Paginación visual: calcula entre qué bloques cortará la impresión cada
  // hoja (misma geometría que el print: alto útil menos el pie repetido, y
  // 18mm extra en la primera hoja si arranca con banner borde a borde) y
  // publica bandas de "cambio de hoja" como decoraciones del plugin.
  // Granularidad de bloque: el corte real puede caer a mitad de un párrafo;
  // la banda se dibuja en la frontera de bloque más cercana.
  useEffect(() => {
    if (!editor || compacto) return undefined;
    let timer = null;
    let ultimaFirma = '';
    const medir = () => {
      const vista = editor.view;
      const { doc } = editor.state;
      const altoPagina = Math.max(200, altoUtilPx - (reservaPie ? reservaPie + 30 : 0));
      const primero = doc.firstChild;
      const esBanner = primero && primero.type.name === 'image' && primero.attrs.fullbleed;
      let capacidad = altoPagina + (esBanner ? supPx : 0);
      let usado = 0;
      const posiciones = [];
      doc.forEach((nodo, offset) => {
        if (nodo.type.name === 'pageBreak') {
          usado = 0;
          capacidad = altoPagina;
          return;
        }
        const dom = vista.nodeDOM(offset);
        if (!(dom instanceof HTMLElement)) return;
        const alto = dom.offsetHeight + 12; // margen aproximado entre bloques
        if (usado > 0 && usado + alto > capacidad) {
          posiciones.push(offset);
          usado = alto;
          capacidad = altoPagina;
        } else {
          usado += alto;
        }
      });
      const firma = `${posiciones.join(',')}|${altoPagina}`;
      if (firma === ultimaFirma) return;
      ultimaFirma = firma;
      const decos = posiciones.map((pos, i) =>
        Decoration.widget(pos, () => crearBandaHoja(i + 2), { side: -1, key: `hoja-${pos}` })
      );
      vista.dispatch(vista.state.tr.setMeta(clavePaginas, DecorationSet.create(doc, decos)));
    };
    const programar = () => {
      clearTimeout(timer);
      timer = setTimeout(medir, 250);
    };
    editor.on('update', programar);
    const pm = hojaRef.current?.querySelector('.ProseMirror');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(programar) : null;
    if (ro && pm) ro.observe(pm);
    programar();
    return () => {
      editor.off('update', programar);
      if (ro) ro.disconnect();
      clearTimeout(timer);
    };
  }, [editor, reservaPie, compacto, firmaHoja]);

  if (!editor) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleWordUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.docx')) {
      onAlert?.('Solo se admiten archivos .docx (Word moderno).', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      onAlert?.('El archivo es muy grande. Máximo 10MB.', 'error');
      return;
    }

    try {
      setImporting(true);
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            "p[style-name='Title'] => h1",
            "p[style-name='Heading 1'] => h1",
            "p[style-name='Heading 2'] => h2",
            "p[style-name='Heading 3'] => h3"
          ]
        }
      );
      editor.commands.setContent(wrapVariableTokens(result.value), { parseOptions: OPCIONES_PARSEO });
      onChange?.(editor.getHTML());
      if (result.messages?.length) {
        const warns = result.messages.filter((m) => m.type === 'warning').length;
        if (warns > 0) {
          onAlert?.(`Documento importado. ${warns} advertencia(s) de formato — revisa el contenido.`, 'warning');
        } else {
          onAlert?.('Documento importado correctamente.', 'success');
        }
      } else {
        onAlert?.('Documento importado correctamente.', 'success');
      }
    } catch (err) {
      console.error('Error importando docx:', err);
      onAlert?.('No se pudo importar el documento.', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      onAlert?.('La imagen es muy grande. Máximo 2MB.', 'error');
      return;
    }
    if (!file.type.startsWith('image/')) {
      onAlert?.('Selecciona un archivo de imagen válido.', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      editor.chain().focus().setImage({ src: ev.target.result }).run();
    };
    reader.readAsDataURL(file);
  };

  const isActive = (name, attrs) => editor.isActive(name, attrs);

  const handleAlign = (alignment) => {
    if (editor.isActive('image')) {
      editor.chain().focus().updateAttributes('image', { align: alignment, wrap: 'none' }).run();
    } else {
      editor.chain().focus().setTextAlign(alignment).run();
    }
  };

  const alignActive = (alignment) => {
    if (editor.isActive('image')) {
      return editor.getAttributes('image').align === alignment;
    }
    return editor.isActive({ textAlign: alignment });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 0.5,
          mb: 1,
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}
      >
        <Button
          size="small"
          variant="contained"
          startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={importing}
          sx={{
            fontWeight: 600,
            mr: 1,
            textTransform: 'none',
            boxShadow: 'none',
            color: '#FFFFFF',
            '&:hover': { boxShadow: 'none', color: '#FFFFFF' }
          }}
        >
          {importing ? 'Importando...' : 'Cargar Word'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx"
          style={{ display: 'none' }}
          onChange={handleWordUpload}
        />

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Deshacer">
          <span>
            <IconButton size="small" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Rehacer">
          <span>
            <IconButton size="small" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Negrita">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleBold().run()} color={isActive('bold') ? 'primary' : 'default'}>
            <FormatBold fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Cursiva">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleItalic().run()} color={isActive('italic') ? 'primary' : 'default'}>
            <FormatItalic fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Subrayado">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleUnderline().run()} color={isActive('underline') ? 'primary' : 'default'}>
            <FormatUnderlined fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Lista con viñetas">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleBulletList().run()} color={isActive('bulletList') ? 'primary' : 'default'}>
            <FormatListBulleted fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Lista numerada">
          <IconButton size="small" onClick={() => editor.chain().focus().toggleOrderedList().run()} color={isActive('orderedList') ? 'primary' : 'default'}>
            <FormatListNumbered fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Alinear izquierda">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlign('left')} color={alignActive('left') ? 'primary' : 'default'}>
            <FormatAlignLeft fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinear centro">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlign('center')} color={alignActive('center') ? 'primary' : 'default'}>
            <FormatAlignCenter fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alinear derecha">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlign('right')} color={alignActive('right') ? 'primary' : 'default'}>
            <FormatAlignRight fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Justificar">
          <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => handleAlign('justify')} color={alignActive('justify') ? 'primary' : 'default'}>
            <FormatAlignJustify fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Disminuir sangría (Shift+Tab)">
          <IconButton size="small" onClick={() => editor.chain().focus().cambiarSangria(-1).run()}>
            <FormatIndentDecrease fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Aumentar sangría (Tab)">
          <IconButton size="small" onClick={() => editor.chain().focus().cambiarSangria(1).run()}>
            <FormatIndentIncrease fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Interlineado">
          <TextField
            select
            size="small"
            value={
              editor.getAttributes('paragraph').lineheight ||
              editor.getAttributes('heading').lineheight ||
              ''
            }
            onChange={(e) => editor.chain().focus().setLineHeight(e.target.value || null).run()}
            SelectProps={{ native: true }}
            sx={{ minWidth: 70, '& .MuiInputBase-root': { height: 32 } }}
            InputProps={{
              startAdornment: <FormatLineSpacing sx={{ fontSize: 16, mr: 0.4, color: '#5f6368' }} />
            }}
          >
            <option value="">1.6</option>
            <option value="1">1.0</option>
            <option value="1.15">1.15</option>
            <option value="1.5">1.5</option>
            <option value="2">2.0</option>
          </TextField>
        </Tooltip>

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Insertar tabla 3×3">
          <IconButton
            size="small"
            onClick={() =>
              editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run()
            }
          >
            <TableChartOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        {editor.isActive('table') && (
          <>
            {[
              { id: 'fila+', label: 'Fila+', accion: () => editor.chain().focus().addRowAfter().run() },
              { id: 'fila-', label: 'Fila−', accion: () => editor.chain().focus().deleteRow().run() },
              { id: 'col+', label: 'Col+', accion: () => editor.chain().focus().addColumnAfter().run() },
              { id: 'col-', label: 'Col−', accion: () => editor.chain().focus().deleteColumn().run() },
              { id: 'unir', label: 'Unir/Dividir', accion: () => editor.chain().focus().mergeOrSplit().run() },
              {
                id: 'bordes',
                label: editor.getAttributes('table').sinbordes ? 'Con bordes' : 'Sin bordes',
                accion: () =>
                  editor
                    .chain()
                    .focus()
                    .updateAttributes('table', {
                      sinbordes: !editor.getAttributes('table').sinbordes
                    })
                    .run()
              },
              { id: 'quitar', label: 'Quitar tabla', accion: () => editor.chain().focus().deleteTable().run() }
            ].map((b) => (
              <Button
                key={b.id}
                size="small"
                onClick={b.accion}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  minWidth: 0,
                  px: 0.8,
                  color: CHIP_BLUE
                }}
              >
                {b.label}
              </Button>
            ))}
          </>
        )}

        <Divider orientation="vertical" flexItem />

        <Tooltip title="Insertar imagen">
          <IconButton size="small" onClick={() => imageInputRef.current?.click()}>
            <ImageIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Salto de página">
          <IconButton
            size="small"
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertContent([{ type: 'pageBreak' }, { type: 'paragraph' }])
                .run()
            }
          >
            <InsertPageBreakIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />

        <Divider orientation="vertical" flexItem />

        <TextField
          select
          size="small"
          value={
            editor.isActive('heading', { level: 1 }) ? 'h1'
              : editor.isActive('heading', { level: 2 }) ? 'h2'
              : editor.isActive('heading', { level: 3 }) ? 'h3'
              : 'p'
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'p') editor.chain().focus().setParagraph().run();
            else editor.chain().focus().toggleHeading({ level: parseInt(v.replace('h', ''), 10) }).run();
          }}
          SelectProps={{ native: true }}
          sx={{ minWidth: 110, '& .MuiInputBase-root': { height: 32 } }}
        >
          <option value="p">Párrafo</option>
          <option value="h1">Título 1</option>
          <option value="h2">Título 2</option>
          <option value="h3">Título 3</option>
        </TextField>

        <Tooltip title="Tamaño de letra (puntos)">
          <TextField
            select
            size="small"
            value={editor.getAttributes('textStyle').fontSize || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v) editor.chain().focus().setFontSize(v).run();
              else editor.chain().focus().unsetFontSize().run();
            }}
            SelectProps={{ native: true }}
            sx={{ minWidth: 88, '& .MuiInputBase-root': { height: 32 } }}
          >
            <option value="">Tamaño</option>
            {TAMANOS_LETRA.map((t) => (
              <option key={t} value={`${t}pt`}>
                {t}
              </option>
            ))}
          </TextField>
        </Tooltip>
      </Paper>

      {/* Lienzo tipo Word: hoja carta (8.5in x 11in) centrada sobre fondo
          gris, con los mismos márgenes (18mm) que usa la impresión, para que
          lo redactado coincida 1:1 con la hoja impresa. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          overflowY: 'auto',
          overflowX: 'auto',
          backgroundColor: '#e8eaed',
          py: 3,
          px: 1,
          '& .ProseMirror': {
            outline: 'none',
            minHeight: compacto
              ? '60px'
              : `calc(${altoHoja} - ${margenes.sup + margenes.inf}mm)`,
            fontFamily: 'Arial, sans-serif',
            lineHeight: 1.6,
            fontSize: '14px',
            // La hoja siempre es papel blanco con tinta negra, aunque la app
            // esté en modo oscuro (el texto heredaba el color del tema).
            backgroundColor: '#FFFFFF',
            color: '#000000'
          },
          '& .ProseMirror p': { margin: '0 0 0.75em' },
          '& .ProseMirror h1': { fontSize: '1.6em', fontWeight: 700, margin: '0.5em 0' },
          '& .ProseMirror h2': { fontSize: '1.35em', fontWeight: 700, margin: '0.5em 0' },
          '& .ProseMirror h3': { fontSize: '1.15em', fontWeight: 700, margin: '0.5em 0' },
          '& .ProseMirror img': { maxWidth: '100%', height: 'auto', display: 'block', margin: '0.5em 0' },
          '& .ProseMirror ul, & .ProseMirror ol': { paddingLeft: '1.5em' },
          '& .ProseMirror table': {
            borderCollapse: 'collapse',
            width: '100%',
            tableLayout: 'fixed',
            margin: '0.5em 0',
            overflow: 'hidden'
          },
          '& .ProseMirror table td, & .ProseMirror table th': {
            border: '1px solid #444',
            padding: '4px 8px',
            verticalAlign: 'top',
            position: 'relative',
            minWidth: '32px'
          },
          '& .ProseMirror table th': { backgroundColor: '#f1f3f4', fontWeight: 700 },
          // Tabla sin bordes: en el editor las celdas conservan un guía
          // punteado suave para poder editarlas; impresión y Word no pintan
          // ningún borde.
          '& .ProseMirror table[data-sinbordes="true"] td, & .ProseMirror table[data-sinbordes="true"] th': {
            border: '1px dashed #c4cdd6'
          },
          '& .ProseMirror table[data-sinbordes="true"] th': { backgroundColor: 'transparent' },
          '& .ProseMirror table .selectedCell:after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(25,118,210,0.12)',
            pointerEvents: 'none'
          },
          '& .ProseMirror table .column-resize-handle': {
            position: 'absolute',
            right: '-2px',
            top: 0,
            bottom: '-2px',
            width: '4px',
            backgroundColor: CHIP_BLUE,
            pointerEvents: 'none'
          },
          '& .ProseMirror.resize-cursor': { cursor: 'col-resize' },
          // Banda de cambio de hoja (paginación visual): gris del fondo con
          // sombras que simulan el borde inferior/superior de las hojas.
          '& .ProseMirror .salto-hoja-visual': {
            width: 'calc(100% + var(--mizq, 18mm) + var(--mder, 18mm))',
            margin: '16px calc(-1 * var(--mder, 18mm)) 16px calc(-1 * var(--mizq, 18mm))',
            height: '34px',
            backgroundColor: '#e8eaed',
            boxShadow:
              'inset 0 7px 6px -6px rgba(0,0,0,0.35), inset 0 -7px 6px -6px rgba(0,0,0,0.35)',
            position: 'relative',
            userSelect: 'none'
          },
          '& .ProseMirror .salto-hoja-visual span': {
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '10px',
            fontWeight: 700,
            color: '#5f6368'
          }
        }}
      >
        <Box
          ref={hojaRef}
          sx={{
            width: anchoHoja,
            minHeight: compacto ? 'auto' : altoHoja,
            mx: 'auto',
            backgroundColor: '#FFFFFF',
            color: '#000000',
            boxShadow: '0 1px 6px rgba(0,0,0,0.28)',
            p: compacto
              ? `6mm ${margenes.der}mm 6mm ${margenes.izq}mm`
              : `${margenes.sup}mm ${margenes.der}mm ${margenes.inf}mm ${margenes.izq}mm`,
            boxSizing: 'border-box',
            position: 'relative',
            // Variables que consumen el banner borde a borde y las bandas de
            // cambio de hoja para salirse de los márgenes configurados.
            '--mizq': `${margenes.izq}mm`,
            '--mder': `${margenes.der}mm`,
            '--msup': `${margenes.sup}mm`
          }}
        >
          {/* Los cambios de hoja se dibujan como decoraciones del editor
              (bandas .salto-hoja-visual insertadas por PaginasVisuales). */}
          <EditorContent editor={editor} />
        </Box>
      </Box>
    </Box>
  );
});

export default TiptapEditor;
